/**
 * One place that says which permission a screen needs.
 *
 * The names here are the seeded catalogue's, exactly — `module.action`, the
 * same strings the API checks and the same ones `/business/permissions`
 * publishes. Both the sidebar and the router read this map, so a menu entry
 * and the page behind it cannot drift apart, and a renamed permission is one
 * edit rather than a hunt through the pages.
 *
 * Each entry is the permission the **server** enforces for that screen's first
 * read, so a page never renders only to fill with 403s — and, since file 03,
 * a user holding only `products.create` passes a `products.view` guard here
 * too: `/business/me` reports the implied read, so `can()` sees it.
 *
 * Hiding a screen is still only a convenience. The API checks every request
 * again; these guards exist so the portal does not offer what the server will
 * refuse, and does not hide what it would allow.
 */
export const ROUTE_PERMISSIONS = {
  "/diary": "appointments.view",
  "/appointments/:id": "appointments.view",

  "/branches": "branches.view",
  "/branches/create": "branches.create",
  "/branches/:id": "branches.view",
  // A branch's own screens answer to the module they are about, not to
  // `branches.*`: the offering list is the service catalogue's decision and
  // the product list is the catalogue's.
  "/branches/:id/services": "services.view",
  "/branches/:id/products": "products.view",

  "/clients": "clients.view",
  "/clients/:id": "clients.view",

  "/services": "services.view",
  "/service-categories": "services.view",

  "/products": "products.view",
  "/products/create": "products.create",
  "/products/:productId/edit": "products.update",
  "/product-categories": "products.view",
  "/brands": "products.view",

  // "Staff" is who may sign in — the `users.*` module. "Providers" is who
  // performs services, which is `staff.*`. The two read the wrong way round
  // in English and are right: the API draws the same line.
  "/staff": "users.view",
  "/providers": "staff.view",
  "/providers/:id": "staff.view",

  "/transactions": "payments.view",

  // Loyalty — also hidden entirely when the plan leaves it out (FEATURE_ROUTES).
  "/loyalty/programs": "loyalty.view",
  "/loyalty/programs/new": "loyalty.manage",
  "/loyalty/programs/:programId": "loyalty.view",
  "/loyalty/programs/:programId/edit": "loyalty.manage",
  "/loyalty/rewards": "loyalty.manage",
  "/loyalty/members": "loyalty.view",
  "/loyalty/members/:clientId": "loyalty.view",
  "/loyalty/redeem": "loyalty.redeem",
  "/reviews": "reviews.view",
  "/alerts": "alerts.view",
  "/reports": "reports.view",
  "/roles": "users.manage_roles",
};

/**
 * The permission a path needs, or undefined when it needs none. The dashboard,
 * the business page and the security page are deliberately absent: the server
 * gates none of them, so neither does the portal.
 */
export const permissionFor = (path) => ROUTE_PERMISSIONS[path];

/**
 * The plan feature a path needs (business.features on /business/me). The
 * server refuses these with 403 plan_feature_unavailable when the plan leaves
 * the feature out.
 */
export const FEATURE_ROUTES = {
  "/loyalty/programs": "loyalty",
  "/loyalty/programs/new": "loyalty",
  "/loyalty/programs/:programId": "loyalty",
  "/loyalty/programs/:programId/edit": "loyalty",
  "/loyalty/rewards": "loyalty",
  "/loyalty/members": "loyalty",
  "/loyalty/members/:clientId": "loyalty",
  "/loyalty/redeem": "loyalty",
};

export const featureFor = (path) => FEATURE_ROUTES[path];
